import { Fragment, useEffect, useState } from 'react';
import { InsightsIcon } from 'assets/icons';
import { Insights, VMiQ, PistilNext } from 'assets';
import { authSelectors, hooks } from 'state';
import { Button, Container, LoadingScreen, Text } from 'components';
import { FiChevronRight, FiActivity, FiArrowRight, FiZap } from 'react-icons/fi';
import { dataTrackingOperations } from 'state/ducks/data-tracking';
import StyledWelcome from './styledWelcome';
import { cookifyString, getSubCookieNames, getDestinationCookieParametersConfig, isSameRoleType } from '../utils';
import {
  getPersistedDestinationApp,
  isExplicitAppDestination,
  resolveStickyDestination,
  shouldApplyLegacySingleRoleRedirect,
} from '../utils/migration';
import { INSIGHTS_KEY, PISTIL_NEXT_KEY } from '../constants/migration';
import { useCookies } from 'react-cookie';

export const Welcome = ({ userManager }: any) => {
  const { useAppSelector, useAppDispatch } = hooks
  const { selectAuth } = authSelectors
  const { trackEvent } = dataTrackingOperations
  let { user } = useAppSelector(selectAuth)
  const [loading, setLoading] = useState(true)
  const originApp = sessionStorage.getItem('originApp')
  const destinationApp = sessionStorage.getItem('destinationApp')
  const dispatch = useAppDispatch()
  const cookieNames = getSubCookieNames();
  const [, setCookie] = useCookies(Object.values(cookieNames));
  const cookieUserConfig = getDestinationCookieParametersConfig();


  let name = '';
  let roles: any = [];
  if (user) {
    roles = user?.profile?.role;
    name = user?.profile?.name;
  }
  const [message, setMessage] = useState(
    {
      title: `Welcome ${name.split(' ')[0]}!`,
      subtitle: 'Choose where you want to start today'
    }
  )

  const showInsights = isSameRoleType(roles, 'PistilInsights')
  const showVmiq = isSameRoleType(roles, 'PistilVmiq')

  const cards = [
    {
      key: 'insights',
      shouldShow: showInsights,
      icon: <img src={InsightsIcon} alt="Insights icon"/>,
      title: <img src={Insights} alt="Insights title logo" />,
      button: (
        <Button
          onClick={() => {
            // Escape hatch: explicitly choosing Insights overwrites the sticky
            // preference, so a migrated user can get back to the chooser.
            setCookie('destinationApp', cookifyString(INSIGHTS_KEY), cookieUserConfig)
            dispatch(trackEvent({ eventCategory: 'switch', values: { appSelection: 'PI' }, event: 'switch_from_menu' }))
            setTimeout(() => { window.location.assign(`${process.env.REACT_APP_INSIGHTS_URL}`) })
          }}
        >
          <Text>GO TO INSIGHTS</Text>
        </Button>
      ),
      redirectUrl: process.env.REACT_APP_INSIGHTS_URL
    },
    {
      key: 'vmiq',
      shouldShow: showVmiq,
      icon: <img src={InsightsIcon} alt="Insights icon"/>,
      title: <img src={VMiQ} alt="VMiQ title logo" />,
      button: (
        <Button
          onClick={() => {
            dispatch(trackEvent({ eventCategory: 'switch', values: { appSelection: 'PV' }, event: 'switch_from_menu' }))
            setTimeout(() => { window.location.assign(`${process.env.REACT_APP_VMIQ_URL}`) })
          }}
        >
          <Text>GO TO VMIQ</Text>
        </Button>
      ),
      redirectUrl: process.env.REACT_APP_VMIQ_URL
    },
    {
      key: 'pistilnext',
      shouldShow: showInsights,
      icon: <img src={InsightsIcon} alt="Pistil Next icon"/>,
      title: <img src={PistilNext} alt="Pistil Next title logo" />,
      button: (
        <Button
          onClick={() => {
            // Persist the choice → future logins auto-redirect (spec §6.2).
            setCookie('destinationApp', cookifyString(PISTIL_NEXT_KEY), cookieUserConfig)
            dispatch(trackEvent({ eventCategory: 'switch', values: { appSelection: 'PN' }, event: 'switch_from_menu' }))
            setTimeout(() => { window.location.assign(`${process.env.REACT_APP_PISTILNEXT_URL}`) })
          }}
        >
          <Text>GO TO PISTIL NEXT</Text>
        </Button>
      ),
      redirectUrl: process.env.REACT_APP_PISTILNEXT_URL
    },
    {
      key:'legacy_insights',
      shouldShow: false,
      icon: <img alt='none'/>,
      title: <img alt='none'></img>,
      button: (
        <Button
          onClick={() => {
            dispatch(trackEvent({ eventCategory: 'switch', values: { appSelection: 'PLI' }, event: 'switch_from_menu' }))
            setTimeout(() => { window.location.assign(`${process.env.REACT_APP_INSIGHTS_LEGACY_URL}`) })
          }}
        >
          <Text>GO TO INSIGHTS</Text>
        </Button>
      ),
      redirectUrl: process.env.REACT_APP_INSIGHTS_LEGACY_URL
    }
  ];
  const getActiveCards = () => {
    const activeCards = [];
    if (showInsights) {
      activeCards.push(cards.find((x) => x.key === 'insights'));
      activeCards.push(cards.find((x) => x.key === 'pistilnext'));
    }
    if (showVmiq) {
      activeCards.push(cards.find((x) => x.key === 'vmiq'));
    }
    return activeCards;
  };

  const renderCards = () =>
    getActiveCards().map(({ icon, title, button, shouldShow, key }: any) => {
      if (shouldShow) {
        return (
          <Container className={`${key} card`} key={Math.random()}>
            <Container className="icon-container">{icon}</Container>
            <Container className="logo-container">{title}</Container>
            {button}
          </Container>
        );
      }
      return <Fragment key={Math.random()} />;
    });

  useEffect(() => {
    setMessage(prev => ({
      ...prev,
      title: `Welcome ${name.split(' ')[0]}!`,
    }))
    if (user) {
      let hasActiveSubscription;
      const noSuscriptions = !roles || (roles && !roles.length)

      // Insights → Pistil Next MIGRATION.
      // For users with Insights arriving WITHOUT an explicit app destination from
      // navigation (explicit destinations are set by another app and handled by the
      // switch below), the chooser choice is the source of truth:
      //   - already chose Pistil Next → auto-redirect to Pistil Next (sticky preference).
      //   - not yet chosen            → show the chooser (Insights + Pistil Next);
      //                                 do NOT auto-redirect to Insights.
      // We return here so we do NOT fall into the role-routing below (case 'landing',
      // the typeof roles === 'string' block, etc.), which would force Insights and hide
      // Pistil Next. Users without Insights fall through to the existing flow unchanged.
      if (!isExplicitAppDestination(destinationApp) && isSameRoleType(roles, 'PistilInsights')) {
        const sticky = resolveStickyDestination(roles, getPersistedDestinationApp());
        if (sticky === PISTIL_NEXT_KEY) {
          dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp: PISTIL_NEXT_KEY }, event: 'on_redirect_from_origin_app' }))
          setTimeout(() => { window.location.assign(`${process.env.REACT_APP_PISTILNEXT_URL}`) })
        } else {
          setLoading(false)
        }
        return;
      }

      switch (destinationApp) {
        case 'landing':
          let isInsights = roles?.includes('PistilInsights')
          if(isInsights){
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp }, event: 'on_redirect_from_origin_app' }))
            setTimeout(() => { window.location.assign(`${process.env.REACT_APP_INSIGHTS_URL}`) })
            break
          }
          let isVmiq = roles?.includes('PistilVmiq')
          if(isVmiq){
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp }, event: 'on_redirect_from_origin_app' }))
            window.location.assign(`${process.env.REACT_APP_VMIQ_URL}`)
          }
          let isLegacyInsights = roles?.includes('PistilLegacy')
          if(isLegacyInsights){
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp }, event: 'on_redirect_from_origin_app' }))
            window.location.assign(`${process.env.REACT_APP_INSIGHTS_LEGACY_URL}`)
          }
          break
        case 'insights':
          setCookie('destinationApp',cookifyString(destinationApp!),cookieUserConfig)
          hasActiveSubscription = roles?.includes('PistilInsights')
          if (hasActiveSubscription) {
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp }, event: 'on_redirect_from_origin_app' }))
            window.location.assign(`${process.env.REACT_APP_INSIGHTS_URL}`)
            break;
          } else {
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp, success: false, message: `don't have an active subscription to Insights` }, event: 'on_redirect_from_origin_app' }))
            setMessage({
              title: `Sorry, you don't have an active subscription to Insights`,
              subtitle: 'Please choose one of your available subscriptions below:'
            })
            setLoading(false)
          }
          break;
        case 'vmiq':
          setCookie('destinationApp',cookifyString(destinationApp!),cookieUserConfig)
          hasActiveSubscription = roles?.includes('PistilVmiq')
          if (hasActiveSubscription) {
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp }, event: 'on_redirect_from_origin_app' }))
            window.location.assign(`${process.env.REACT_APP_VMIQ_URL}`)
            break;
          } else {
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp, success: false, message: `don't have an active subscription to VMiQ` }, event: 'on_redirect_from_origin_app' }))
            setMessage({
              title: `Sorry, you don't have an active subscription to VMiQ`,
              subtitle: 'Please choose one of your available subscriptions below:'
            })
            setLoading(false)
          }
          break;
        case 'pistilnext':
          setCookie('destinationApp',cookifyString(destinationApp!),cookieUserConfig)
          hasActiveSubscription = roles?.includes('PistilInsights')
          if (hasActiveSubscription) {
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp }, event: 'on_redirect_from_origin_app' }))
            window.location.assign(`${process.env.REACT_APP_PISTILNEXT_URL}`)
            break;
          } else {
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp, success: false, message: `don't have access to Pistil Next` }, event: 'on_redirect_from_origin_app' }))
            setMessage({
              title: `Sorry, you don't have access to Pistil Next`,
              subtitle: 'Please choose one of your available subscriptions below:'
            })
            setLoading(false)
          }
          break;
        case 'legacy_insights':
          setCookie('destinationApp',cookifyString(destinationApp!),cookieUserConfig)
          hasActiveSubscription = roles.includes('PistilLegacy')
          if(hasActiveSubscription) {
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp }, event: 'on_redirect_from_origin_app' }))
            window.location.assign(`${process.env.REACT_APP_INSIGHTS_LEGACY_URL}`)
            break;
          }else{
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp, success: false, message: `don't have an active subscription to Legacy Insights` }, event: 'on_redirect_from_origin_app' }))
            setMessage({
              title: `Sorry, you don't have an active subscription to Legacy Insights`,
              subtitle: 'Please choose one of your available subscriptions below:'
            })
            setLoading(false)
          }
          break;
        default:
          if (getActiveCards().length === 1) {
            dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp: getActiveCards()[0]?.key }, event: 'on_single_subscription' }))
            setTimeout(() => { window.location.assign(`${getActiveCards()[0]?.redirectUrl}`) })
          } else {
            setLoading(false)
          }
          break;
      }
      // A `break` above exits the SWITCH, not this effect. This legacy
      // single-role fallback must therefore stay OUT of the way whenever the
      // switch already routed an explicit destination — otherwise it rewrites
      // the destinationApp cookie to 'insights' and schedules a second
      // window.location.assign that wins the FIFO race, bouncing
      // "Go to Pistil Next" straight back to Insights.
      if(shouldApplyLegacySingleRoleRedirect(destinationApp, roles)){
        if(roles === 'PistilInsights'){
          setCookie('destinationApp',cookifyString('insights'),cookieUserConfig)
          dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp }, event: 'on_redirect_from_origin_app' }))
          setTimeout(() => { window.location.assign(`${process.env.REACT_APP_INSIGHTS_URL}`) })
        } else if(roles === 'PistilVmiq'){
          setCookie('destinationApp',cookifyString('vmiq'),cookieUserConfig)
          dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp }, event: 'on_redirect_from_origin_app' }))
          setTimeout(() => { window.location.assign(`${process.env.REACT_APP_VMIQ_URL}`) })
        } else if(roles === 'PistilLegacy'){
          setCookie('destinationApp',cookifyString('legacy_insights'),cookieUserConfig)
          dispatch(trackEvent({ eventCategory: 'switch', values: { originApp, destinationApp }, event: 'on_redirect_from_origin_app' }))
          setTimeout(() => { window.location.assign(`${process.env.REACT_APP_INSIGHTS_LEGACY_URL}`) })
        }
      }
      if (noSuscriptions) {
        window.location.assign(`${process.env.REACT_APP_INSIGHTS_URL}`)
        setMessage({
          title: "Sorry, you don't have any active subscription",
          subtitle: 'Please contact an administrator'
        })
        setLoading(false)
      }
    }
    // This effect intentionally runs only when `user` resolves — it performs the
    // one-shot routing/redirect decision. Adding the other referenced values
    // (getActiveCards, cookieUserConfig, trackEvent…) would re-run it on every
    // render and re-fire the redirects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const goToPistilNext = () => {
    // Persist the choice → future logins auto-redirect (spec §6.2).
    setCookie('destinationApp', cookifyString(PISTIL_NEXT_KEY), cookieUserConfig)
    dispatch(trackEvent({ eventCategory: 'switch', values: { appSelection: 'PN' }, event: 'switch_from_menu' }))
    setTimeout(() => { window.location.assign(`${process.env.REACT_APP_PISTILNEXT_URL}`) })
  }
  const goToInsights = () => {
    // Escape hatch: choosing Insights overwrites the sticky preference.
    setCookie('destinationApp', cookifyString(INSIGHTS_KEY), cookieUserConfig)
    dispatch(trackEvent({ eventCategory: 'switch', values: { appSelection: 'PI' }, event: 'switch_from_menu' }))
    setTimeout(() => { window.location.assign(`${process.env.REACT_APP_INSIGHTS_URL}`) })
  }
  const legacyItems: Array<{ key: string; label: string; buttonLabel: string; onClick: () => void }> = [];
  if (showInsights) legacyItems.push({ key: 'insights', label: 'Pistil Insights', buttonLabel: 'Go to Insights', onClick: goToInsights });

  if (!loading && roles) return (
    <StyledWelcome className="welcome">
      {showInsights ? (
        <div className="hero">
          <div className="hero-head">
            <span className="update-badge"><FiZap size={13} /> Platform Update</span>
            <h1 className="hero-title">
              We have launched our new application, <span className="accent">Pistil Next</span>,
            </h1>
            <p className="hero-sub">with more data and many new features. Click below to check it out.</p>
          </div>
          <div className="hero-cards">
            <div className="pn-card">
              <div className="pn-card-top">
                <span className="pn-icon"><FiActivity size={24} /></span>
                <span className="live-badge"><span className="live-dot" /> LIVE NOW</span>
              </div>
              <h2 className="pn-title">Pistil Next</h2>
              <p className="pn-desc">
                The next generation of cannabis market intelligence. Faster, deeper, and completely redesigned for clarity and precision.
              </p>
              <div className="pn-divider" />
              <button type="button" className="pn-btn" onClick={goToPistilNext}>
                Go to Pistil Next <FiArrowRight size={18} />
              </button>
            </div>
            {legacyItems.length > 0 && (
              <div className="legacy-col">
                {legacyItems.map((item) => (
                  <div className="legacy-card" key={item.key}>
                    <div className="legacy-top">
                      <div>
                        <h3 className="legacy-title">{item.label}</h3>
                        <span className="legacy-caption">Previous version</span>
                      </div>
                      <span className="legacy-icon"><FiActivity size={18} /></span>
                    </div>
                    <button type="button" className="legacy-btn" onClick={item.onClick}>
                      {item.buttonLabel} <FiChevronRight size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <Container className="title-section">
            <Text textStyle='h1'>{message.title}</Text>
            <Text textStyle='h3'>{message.subtitle}</Text>
          </Container>
          <Container className="cards-section">
            {renderCards()}
          </Container>
        </>
      )}
    </StyledWelcome>
  );
  return <LoadingScreen />
};

export default Welcome;
